import React, {useCallback, useEffect, useState} from "react";
import { Button, Modal } from "antd";
import { useTranslation } from '../../hooks/useTranslation';

type NoticeDisplayType = 'FLOATING' | 'MODAL';
type NoticeLevel = 'info' | 'warning' | 'error' | 'success';

interface NoticeData {
  from: string;
  to: string;
  title: string;
  id: string;
  condition?: string;
  content: string;
  times?: number; // 可选字段：展示次数限制
  displayType: NoticeDisplayType;
  level: NoticeLevel;
  actionText?: string;
  actionUrl?: string;
  preservePath: boolean;
  dismissible: boolean;
}

// 后端返回的公告数据格式
interface BackendNotice {
  id: number;
  noticeId: string;
  title: string;
  content: string;
  fromTime: string;
  toTime: string;
  condition?: string;
  times?: number;
  enabled: boolean;
  displayType?: string;
  level?: string;
  actionText?: string;
  actionUrl?: string;
  preservePath?: boolean;
  dismissible?: boolean;
}

const getNoticeDisplayType = (displayType?: string): NoticeDisplayType =>
  displayType === 'MODAL' ? 'MODAL' : 'FLOATING';

const getNoticeLevel = (level?: string): NoticeLevel => {
  if (level === 'warning' || level === 'error' || level === 'success') {
    return level;
  }
  return 'info';
};

const noticeLevelStyles: Record<NoticeLevel, { color: string; borderColor: string; background: string; icon: string }> = {
  info: { color: '#1677ff', borderColor: '#d9d9d9', background: '#fff', icon: 'i' },
  warning: { color: '#d48806', borderColor: '#ffd666', background: '#fffbe6', icon: '!' },
  error: { color: '#cf1322', borderColor: '#ffa39e', background: '#fff1f0', icon: '!' },
  success: { color: '#389e0d', borderColor: '#95de64', background: '#f6ffed', icon: '✓' },
};

const RightLink = () => {
  const { t } = useTranslation();
  const [list, setList] = useState<{link: string; text: string;}[]>();
  const [notice, setNotice] = useState<NoticeData | null>(null);
  const [showNotice, setShowNotice] = useState(false);
  const [hasActiveNotice, setHasActiveNotice] = useState(false);
  const [lastNoticeId, setLastNoticeId] = useState<string | null>(null);

  // 获取链接列表
  useEffect(() => {
    fetch('/config/list').then(res => res.json()).then(json => {
      setList(json);
    })
  }, [])

  // 检查公告展示次数是否达到限制
  const checkNoticeTimesLimit = (notice: NoticeData): boolean => {
    if (!notice.times) return false; // 没有次数限制
    
    const noticeViewCounts = JSON.parse(localStorage.getItem('noticeViewCounts') || '{}');
    const currentCount = noticeViewCounts[notice.id] || 0;
    
    return currentCount >= notice.times;
  };

  // 增加公告展示次数
  const incrementNoticeViewCount = (noticeId: string) => {
    const noticeViewCounts = JSON.parse(localStorage.getItem('noticeViewCounts') || '{}');
    noticeViewCounts[noticeId] = (noticeViewCounts[noticeId] || 0) + 1;
    localStorage.setItem('noticeViewCounts', JSON.stringify(noticeViewCounts));
  };

  // 将后端数据格式转换为前端格式
  const convertBackendNoticeToFrontend = (backendNotice: BackendNotice): NoticeData => {
    return {
      id: backendNotice.noticeId || String(backendNotice.id),
      title: backendNotice.title,
      content: backendNotice.content,
      from: backendNotice.fromTime,
      to: backendNotice.toTime,
      condition: backendNotice.condition,
      times: backendNotice.times || 0,
      displayType: getNoticeDisplayType(backendNotice.displayType),
      level: getNoticeLevel(backendNotice.level),
      actionText: backendNotice.actionText || '',
      actionUrl: backendNotice.actionUrl || '',
      preservePath: !!backendNotice.preservePath,
      dismissible: backendNotice.dismissible !== false,
    };
  };

  // 获取公告数据
  const fetchNotice = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        t: String(new Date().getTime()),
        clientDomain: window.location.hostname,
      });
      // 语种从后端通过 cookie i18nextLng 解析，这里不再显式传递
      const response = await fetch(`/api/notice/active?${params.toString()}`);
      
      if (response.ok) {
        const result = await response.json();
        
        // 检查响应格式
        if (!result.success || !result.data) {
          console.warn('获取公告失败: 响应格式不正确', result);
          return;
        }
        
        const backendNotices: BackendNotice[] = result.data;
        
        // 后端已经过滤了时间、启用状态和投放人群；前端只做展示安全兜底。
        const now = new Date();
        const active = backendNotices
          .map(convertBackendNoticeToFrontend)
          .find(notice => {
            // 双重检查时间（虽然后端已过滤，但前端也检查一次以确保安全）
            const fromTime = new Date(notice.from);
            const toTime = new Date(notice.to);
            const timeValid = now >= fromTime && now <= toTime;
            
            if (!timeValid) return false;

            // 复杂投放规则在后端执行，避免在浏览器执行公告配置中的脚本。
            return true;
          });
        
        if (active) {
          // 检查是否被永久关闭
          const closedNotices = JSON.parse(localStorage.getItem('closedNotices') || '[]');
          if (!closedNotices.includes(active.id)) {
            // 检查展示次数是否达到限制
            if (!checkNoticeTimesLimit(active)) {
              // 检查是否是新的公告
              const isNewNotice = lastNoticeId !== active.id;
              
              setNotice(active);
              setHasActiveNotice(true);
              
              // 只有发现新公告时才自动弹框
              if (isNewNotice) {
                setShowNotice(true);
                setLastNoticeId(active.id);
                // 记录展示次数
                incrementNoticeViewCount(active.id);
              }
            }
          }
        } else {
          // 如果没有生效的公告，清除状态
          setHasActiveNotice(false);
          setNotice(null);
        }
      }
    } catch (err) {
      console.error('获取公告失败:', err);
    }
  }, [lastNoticeId]);

  // 显示公告弹窗
  const handleShowNotice = () => {
    setShowNotice(true);
  };

  // 关闭公告弹窗
  const handleCloseNotice = () => {
    setShowNotice(false);
  };

  // 永久关闭公告
  const handlePermanentClose = () => {
    if (notice) {
      const closedNotices = JSON.parse(localStorage.getItem('closedNotices') || '[]');
      if (!closedNotices.includes(notice.id)) {
        closedNotices.push(notice.id);
        localStorage.setItem('closedNotices', JSON.stringify(closedNotices));
      }
      setHasActiveNotice(false);
      setShowNotice(false);
    }
  };

  const getNoticeActionUrl = (notice: NoticeData): string => {
    if (!notice.actionUrl) {
      return '';
    }
    try {
      const actionUrl = new URL(notice.actionUrl, window.location.href);
      if (notice.preservePath) {
        actionUrl.pathname = window.location.pathname;
        actionUrl.search = window.location.search;
        actionUrl.hash = window.location.hash;
      }
      return actionUrl.toString();
    } catch (error) {
      console.warn('公告按钮链接无效:', notice.actionUrl);
      return '';
    }
  };

  const handleNoticeAction = () => {
    if (!notice) {
      return;
    }
    const actionUrl = getNoticeActionUrl(notice);
    if (actionUrl) {
      window.location.href = actionUrl;
    }
  };

  useEffect(() => {
    fetchNotice();
    
    // 每分钟检查一次是否有新的生效公告
    const interval = setInterval(() => {
      fetchNotice();
    }, 60000);
    
    return () => clearInterval(interval);
  }, [fetchNotice]);

  // 在window对象上添加手动触发公告的方法
  useEffect(() => {
    // 模拟公告数据
    const mockNotice: NoticeData = {
      id: 'mock-notice-' + Date.now(),
      title: '测试公告',
      content: '这是一个测试公告，用于演示公告弹框效果。你可以通过调用 window.showMockNotice() 来手动触发这个公告。',
      from: new Date().toISOString(),
      to: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24小时后过期
      times: 5, // 最多显示5次
      displayType: 'FLOATING',
      level: 'info',
      actionText: '',
      actionUrl: '',
      preservePath: false,
      dismissible: true,
    };

    // 添加手动触发方法到window对象
    (window as any).showMockNotice = () => {
      setNotice(mockNotice);
      setHasActiveNotice(true);
      setShowNotice(true);
      setLastNoticeId(mockNotice.id);
      incrementNoticeViewCount(mockNotice.id);
    };

    // 添加清除公告方法
    (window as any).clearMockNotice = () => {
      setShowNotice(false);
      setHasActiveNotice(false);
      setNotice(null);
    };

    // 清理函数
    return () => {
      delete (window as any).showMockNotice;
      delete (window as any).clearMockNotice;
    };
  }, []);

  // 添加键盘ESC键关闭弹窗功能
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showNotice) {
        handleCloseNotice();
      }
    };

    if (showNotice) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showNotice]);

  return (
    <>
      {
        list && list.map(item => {
          return (
              <a href={item.link} style={{color: 'red', paddingLeft: '5px'}}>{item.text}</a>
          )
        })
      }
      {
        !list && (
          <>
            <a href={"https://z.wiki/jsbin/tikiviwute"} style={{color: 'red'}}>{t('rightLink.usageNotice')}</a> &nbsp;
            <a href={"https://z.wiki/jsbin/waleyijila"} style={{}}>{t('rightLink.supportUs')}</a>
          </>
        )
      }
      
      {/* 公告入口 */}
      {hasActiveNotice && (
        <button 
          onClick={handleShowNotice} 
          style={{
            color: '#ff6b6b', 
            paddingLeft: '5px', 
            cursor: 'pointer',
            textDecoration: 'underline',
            background: 'none',
            border: 'none',
            font: 'inherit'
          }}
        >
          {t('rightLink.notice')}
        </button>
      )}

      {/* 右上角公告弹框 */}
      {showNotice && notice && notice.displayType === 'FLOATING' && (
        <div 
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: noticeLevelStyles[notice.level].background,
            borderRadius: '8px',
            padding: '16px',
            maxWidth: '350px',
            width: 'auto',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            border: `1px solid ${noticeLevelStyles[notice.level].borderColor}`,
            zIndex: 1000,
            transform: 'translateX(0)',
            opacity: 1,
            transition: 'transform 0.3s ease-out, opacity 0.3s ease-out'
          }}
        >
          {/* 关闭按钮 */}
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            display: 'flex',
            gap: '4px'
          }}>
            <button 
              onClick={handleCloseNotice}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#999',
                padding: '2px',
                lineHeight: '1'
              }}
              title={t('common.close')}
            >
              ×
            </button>
          </div>

          {/* 公告内容 */}
          <h4 style={{ 
            margin: '0 0 8px 0', 
            color: noticeLevelStyles[notice.level].color,
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            <span style={{ marginRight: 6 }}>{noticeLevelStyles[notice.level].icon}</span>
            {notice.title}
          </h4>
          <div 
            style={{ 
              lineHeight: '1.4', 
              color: '#666',
              fontSize: '12px',
              marginBottom: '8px',
              maxHeight: '80px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical'
            }}
            dangerouslySetInnerHTML={{ __html: notice.content }}
          />
          
          {/* 操作按钮 */}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginTop: '8px',
            flexWrap: 'wrap'
          }}>
            {notice.actionUrl && (
              <button
                onClick={handleNoticeAction}
                style={{
                  background: noticeLevelStyles[notice.level].color,
                  border: `1px solid ${noticeLevelStyles[notice.level].color}`,
                  borderRadius: '4px',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  color: '#fff'
                }}
              >
                {notice.actionText || '打开链接'}
              </button>
            )}
            <button 
              onClick={handleCloseNotice}
              style={{
                background: '#f5f5f5',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: '11px',
                color: '#666'
              }}
            >
              {t('common.close')}
            </button>
            {notice.dismissible && (
              <button
                onClick={handlePermanentClose}
                style={{
                  background: 'none',
                  border: '1px solid #ff6b6b',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  color: '#ff6b6b'
                }}
              >
                {t('rightLink.dontRemind')}
              </button>
            )}
          </div>
        </div>
      )}
      {notice && (
        <Modal
          open={showNotice && notice.displayType === 'MODAL'}
          title={
            <span style={{ color: noticeLevelStyles[notice.level].color }}>
              <span style={{ marginRight: 8 }}>{noticeLevelStyles[notice.level].icon}</span>
              {notice.title}
            </span>
          }
          onCancel={handleCloseNotice}
          footer={[
            notice.dismissible ? (
              <Button key="dismiss" danger onClick={handlePermanentClose}>
                {t('rightLink.dontRemind')}
              </Button>
            ) : null,
            <Button key="close" onClick={handleCloseNotice}>
              {t('common.close')}
            </Button>,
            notice.actionUrl ? (
              <Button key="action" type="primary" onClick={handleNoticeAction}>
                {notice.actionText || '打开链接'}
              </Button>
            ) : null,
          ].filter(Boolean)}
        >
          <div
            style={{
              padding: '8px 0',
              lineHeight: 1.7,
              color: '#4C5362',
            }}
            dangerouslySetInnerHTML={{ __html: notice.content }}
          />
        </Modal>
      )}
    </>
  )
}

export default RightLink;
